import React, { useState } from 'react';
import { styled } from '@this/constants/themes';
import { withTheme } from 'styled-components';
import _ from 'lodash';

import FlightSeatMapModal from '@this/components/reserve_trip/reserve_confirm/flight_seat_map_modal';
import { Button } from '@material-ui/core';
import Transit from '@this/domain/transit/transit';
import Flight from '@this/domain/flight/flight';
import Hotel from '@this/domain/hotel/hotel';
import MarketLogHelper from '@this/domain/market_log/market_log_helper';
import type TravelerList from '@this/domain/traveler/traveler_list';
import { Text } from '@this/components/shared/ui/data_displays/typography';

import { Flex } from '@this/shared/ui/layout/flex';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import ExternalLinkIcon from '@this/src/components/shared/icons/external_link_icon';
import ReserveConfirmTransitTr from '../tripelement_tr/transit_tr';
import ReserveConfirmHotelTr from '../tripelement_tr/hotel_tr';
import ReserveConfirmFlightTr from '../tripelement_tr/flight_tr';

type Props = {
  isPackage: boolean;
  domesticAirPriceIndex: number | null | undefined;
  elementType: 'transport' | 'hotel' | 'flight' | undefined;
  element: Transit | Hotel | Flight;
  elementRaw: any;
  foreignExist: boolean;
  // shinkansenTooLate: boolean;
  index: number | string | undefined;
  flightDescription: string | undefined;
  deadline: string | undefined;
  minirule: string | undefined;
  flightPrice: number | null;
  hotelPrice: number;
  peoplenum: number;
  roomnum: number | undefined;
  travelers: TravelerList;
  theme: { serviceName: string };
};

const ReserveConfirmOutlineItemTemplate: React.SFC<Props> = ({
  isPackage,
  domesticAirPriceIndex,
  elementType,
  element,
  elementRaw,
  foreignExist,
  // shinkansenTooLate,
  index,
  flightDescription,
  deadline,
  minirule,
  flightPrice,
  peoplenum,
  hotelPrice,
  roomnum,
  travelers,
  theme
}) => {
  const [showFlightSeatMpaModal, setShowFlightSeatMapModal] = useState(false);
  const itemTestIdPrefix = `reserve-confirm-summary-item-${index}`;

  return (
    <ReserveConfirmRightSection data-testid="reserve-confirm-summary-item" id={itemTestIdPrefix}>
      {elementType === 'transport' && element instanceof Transit ? (
        <>
          <ReserveConfirmRightTitle>
            <p data-testid={`${itemTestIdPrefix}-type`}>{element.reserveConfirmElementLabel()}</p>
            <p>
              {!element.package && (
                <>
                  <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-price`}>
                    {utils.digits(element.reserveConfirmElementPrice())}
                  </span>
                  円
                </>
              )}
            </p>
          </ReserveConfirmRightTitle>
          <ReserveConfirmRightContent>
            <ReserveConfirmTransitTr
              itemTestIdPrefix={itemTestIdPrefix}
              transit={element}
              elementRaw={elementRaw}
              domesticAirPriceIndex={domesticAirPriceIndex}
            />
            {MarketLogHelper.isObjectMarketLogLinkAvailable(element, theme.serviceName) && (
              <Link
                href={MarketLogHelper.objectMarketLogPath(element, domesticAirPriceIndex)}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>マーケットログを確認</div>
                <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                  <ExternalLinkIcon />
                </div>
              </Link>
            )}
          </ReserveConfirmRightContent>
        </>
      ) : elementType === 'flight' && element instanceof Flight ? (
        <>
          <ReserveConfirmRightTitle>
            <p data-testid={`${itemTestIdPrefix}-type`}>飛行機</p>
            <p>
              <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-price`}>
                {utils.digits(element.price)}
              </span>
              円
            </p>
          </ReserveConfirmRightTitle>
          <ReserveConfirmRightContent>
            <Flex alignItems="center">
              <Text color="description" level="caption">
                発券期限：
              </Text>
              <Text color="danger" level="caption" bold>
                {element.itineraries.length > 0 && element.itineraries[0].rawTicketingExpiredAt ? (
                  <span data-wovn-ignore>
                    {element.itineraries[0].rawTicketingExpiredAt.format('YYYY/MM/DD HH:mm')}
                  </span>
                ) : (
                  <span data-wovn-ignore>ご依頼から{deadline}時間</span>
                )}
              </Text>
            </Flex>
            <br />
            <ReserveConfirmFlightTr flight={element} />
            {foreignExist && MarketLogHelper.isObjectMarketLogLinkAvailable(element, theme.serviceName) && (
              <Link
                href={MarketLogHelper.objectMarketLogPath(element)}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>マーケットログを確認</div>
                <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                  <ExternalLinkIcon />
                </div>
              </Link>
            )}
            {element.seatReservationEnable && (
              <Button
                color="primary"
                onClick={() => {
                  setShowFlightSeatMapModal(true);
                }}
              >
                座席指定する
              </Button>
            )}
            <FlightSeatMapModal
              show={showFlightSeatMpaModal}
              onHide={() => {
                setShowFlightSeatMapModal(false);
              }}
              flight={element}
              travelers={travelers}
            />
          </ReserveConfirmRightContent>
        </>
      ) : elementType === 'hotel' && element instanceof Hotel ? (
        <>
          <ReserveConfirmRightTitle>
            <p data-testid={`${itemTestIdPrefix}-type`}>宿泊</p>
            <p>
              <span data-wovn-ignore data-testid={`${itemTestIdPrefix}-price`}>
                {utils.digits(element.price || 0)}
              </span>
              円
            </p>
          </ReserveConfirmRightTitle>
          <ReserveConfirmRightContent>
            <ReserveConfirmHotelTr
              hotel={element}
              isPackage={isPackage}
              hotelPrice={hotelPrice}
              itemTestIdPrefix={itemTestIdPrefix}
              peoplenum={peoplenum}
              roomnum={roomnum}
            />
            {!isPackage && (
              <div>
                <Link href={element.detail_path || undefined} isExternal target="_blank" rel="noopener noreferrer">
                  詳細を確認
                </Link>
              </div>
            )}
            {MarketLogHelper.isObjectMarketLogLinkAvailable(element, theme.serviceName) && (
              <Link
                href={MarketLogHelper.objectMarketLogPath(element)}
                isExternal
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <div>マーケットログを確認</div>
                <div style={{ marginTop: '2px', marginLeft: '2px' }}>
                  <ExternalLinkIcon />
                </div>
              </Link>
            )}
          </ReserveConfirmRightContent>
        </>
      ) : null}
    </ReserveConfirmRightSection>
  );
};

const ReserveConfirmRightSection = styled.div`
  margin-bottom: 15px;
`;

const ReserveConfirmRightTitle = styled.div`
  font-weight: bold;
  color: #959595;
  padding: 10px 10px 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReserveConfirmRightContent = styled.div`
  font-weight: bold;
  color: #959595;
  padding: 6px 10px 10px 10px;
`;

export default withTheme(ReserveConfirmOutlineItemTemplate);
